import React from 'react'
import { useSlideIntersectionObserver } from './carousel-helpers'
import { useCarouselContext } from './carousel-context'

const getSlideWidth = (count: number, wrapAround?: boolean): string => `${wrapAround ? 100 / (3 * count) : 100 / count}%`

const getSlideStyles = (
  count: number,
  isVisibleSlide: boolean,
  wrapAround: boolean,
  cellSpacing: number,
  adaptiveHeight: boolean,
  initializedAdaptiveHeight: boolean
): React.CSSProperties => {
  const width = getSlideWidth(count, wrapAround)
  // const width = getSlideWidth(count, wrapAround);

  let height = 'auto'
  if (adaptiveHeight) {
    if (initializedAdaptiveHeight) {
      // Once adaptiveHeight is initialized, the frame will size to the height
      // of all the visible slides
      height = '100%'
    } else if (isVisibleSlide) {
      // If the slide is visible but we're still measuring heights, have
      // visible slides just take up their natural height
      height = 'auto'
    } else {
      // If the slide is not visible and we're still measuring heights, the
      // slide should have height 0 so it doesn't contribute to the measured
      // height of the frame
      height = '0'
    }
  }

  return {
    width,
    height,
    padding: `0 ${cellSpacing ? cellSpacing / 2 : 0}px`
  }
}

const generateIndex = (index: number, count: number, typeOfSlide?: 'prev-cloned' | 'next-cloned'): number => {
  if (typeOfSlide === 'prev-cloned') {
    return index - count
  }

  if (typeOfSlide === 'next-cloned') {
    return index + count
  }

  return index
}

export const SlideContext = React.createContext<{
  index: number
  typeOfSlide: 'prev-cloned' | 'next-cloned' | undefined
} | null>(null)

export interface SlideProps {
  children: React.ReactNode | React.ReactNode[]
  role?: React.AriaRole | undefined
  ['aria-roledescription']?: string
  cellSpacing?: number
  className?: string
}

export function Slide({
  children,
  role,
  'aria-roledescription': ariaRoleDescription,
  cellSpacing = 0,
  className
}: SlideProps) {
  const {
    slideCount,
    wrapAround,
    slideContainerRef,
    carouselId,
    initializedAdaptiveHeight,
    adaptiveHeight,
    onVisibleSlideHeightChange
  } = useCarouselContext()
  const context = React.useContext(SlideContext)
  if (!context) {
    throw new Error('Slide Context Not Found')
  }
  const { index, typeOfSlide } = context
  const customIndex = wrapAround ? generateIndex(index, slideCount, typeOfSlide) : index

  const slideRef = React.useRef<HTMLDivElement>(null)

  const entry = useSlideIntersectionObserver(slideRef, slideContainerRef, () => {})

  const isVisible = !!entry?.isIntersecting
  const isFullyVisible = (entry?.intersectionRatio ?? 1) >= 0.95

  const prevIsVisibleRef = React.useRef(false)
  React.useEffect(() => {
    const node = slideRef.current
    if (node) {
      const slideHeight = node.getBoundingClientRect()?.height

      const prevIsVisible = prevIsVisibleRef.current
      if (isVisible && !prevIsVisible) {
        onVisibleSlideHeightChange(customIndex, slideHeight)
      } else if (!isVisible && prevIsVisible) {
        onVisibleSlideHeightChange(customIndex, null)
      }

      prevIsVisibleRef.current = isVisible
    }
  }, [customIndex, isVisible, onVisibleSlideHeightChange])

  return (
    <div
      ref={slideRef}
      inert={isFullyVisible ? undefined : true}
      className={className}
      style={getSlideStyles(slideCount, isFullyVisible, wrapAround, cellSpacing, adaptiveHeight, initializedAdaptiveHeight)}
      id={typeOfSlide ? undefined : `${carouselId}-slide-${index + 1}`}
      role={role ?? 'group'}
      aria-roledescription={ariaRoleDescription ?? 'slide'}
    >
      {children}
    </div>
  )
}
